import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { Phone, Menu, X, ChevronDown } from "lucide-react";
import logo from "@/assets/oz-property-logo.webp";

export const PHONE_DISPLAY = "1800 556 654";
export const PHONE_HREF = "tel:1800556654";

const navLinks = [
  { label: "Home", to: "/" },
  { label: "Services", to: "/services" },
  { label: "Packages", to: "/packages" },
  { label: "Our Work", to: "/our-work" },
  { label: "Service Areas", to: "/service-areas" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
];

/* Breakpoints for progressive disclosure (Tailwind prefixes):
   xl  = 1280px  → show all 7
   lg  = 1024px  → show first 4, rest in More
   md  = 768px   → show first 2, rest in More
   <md            → hamburger drawer (existing mobile menu)
*/
const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef<HTMLDivElement>(null);

  // Close More dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (moreRef.current && !moreRef.current.contains(event.target as Node)) {
        setMoreOpen(false);
      }
    };
    if (moreOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [moreOpen]);

  // Close More dropdown on Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMoreOpen(false);
    };
    if (moreOpen) {
      document.addEventListener("keydown", handleKeyDown);
    }
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [moreOpen]);

  const visibleAlways = navLinks.slice(0, 2);   // Home, Services  (shown md+)
  const visibleLg = navLinks.slice(2, 4);       // Packages, Our Work (shown lg+)
  const moreItems = navLinks.slice(4);          // Service Areas, About, Contact (in More at lg)
  const moreItemsMd = navLinks.slice(2);          // Packages, Our Work, Service Areas, About, Contact (in More at md)

  return (
    <header className="sticky top-0 z-50 bg-[#FFC300]">
      <div className="container flex flex-nowrap items-center justify-between gap-4 py-3">
        {/* LEFT: Hamburger + Logo */}
        <div className="flex flex-nowrap items-center gap-3 min-w-0">
          <button
            className="text-[#1a1a1a] p-1 md:hidden"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            onClick={() => setMenuOpen(!menuOpen)}
          >
            {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
          <Link to="/" onClick={() => setMenuOpen(false)} className="shrink-0">
            <img
              src={logo}
              alt="Oz Property Services Co. logo"
              className="h-14 md:h-20 w-auto object-contain"
            />
          </Link>

          {/* Desktop nav links — md+ */}
          <nav className="hidden md:flex flex-nowrap items-center gap-4 lg:gap-5 ml-2 lg:ml-4">
            {/* Always visible: Home, Services */}
            {visibleAlways.map((link) => (
              <Link
                key={link.label}
                to={link.to}
                className="text-[#1a1a1a]/80 font-semibold text-sm hover:text-[#1a1a1a] transition-colors whitespace-nowrap"
              >
                {link.label}
              </Link>
            ))}

            {/* Visible at lg+: Packages, Our Work */}
            {visibleLg.map((link) => (
              <Link
                key={link.label}
                to={link.to}
                className="hidden lg:inline text-[#1a1a1a]/80 font-semibold text-sm hover:text-[#1a1a1a] transition-colors whitespace-nowrap"
              >
                {link.label}
              </Link>
            ))}

            {/* Visible at xl+: Service Areas, About, Contact */}
            {moreItems.map((link) => (
              <Link
                key={link.label}
                to={link.to}
                className="hidden xl:inline text-[#1a1a1a]/80 font-semibold text-sm hover:text-[#1a1a1a] transition-colors whitespace-nowrap"
              >
                {link.label}
              </Link>
            ))}

            {/* More dropdown — shown when some items are hidden */}
            <div className="relative xl:hidden" ref={moreRef}>
              <button
                onClick={() => setMoreOpen(!moreOpen)}
                className="flex items-center gap-1 text-[#1a1a1a]/80 font-semibold text-sm hover:text-[#1a1a1a] transition-colors whitespace-nowrap"
                aria-expanded={moreOpen}
                aria-haspopup="menu"
              >
                More
                <ChevronDown
                  className={`w-4 h-4 transition-transform ${moreOpen ? "rotate-180" : ""}`}
                />
              </button>

              {moreOpen && (
                <div className="absolute top-full left-0 mt-2 min-w-[180px] bg-white rounded-xl shadow-lg border border-[#1a1a1a]/10 py-2 z-50">
                  {/* md to lg-1: show items 3-7 in More */}
                  <div className="lg:hidden">
                    {moreItemsMd.map((link) => (
                      <Link
                        key={link.label}
                        to={link.to}
                        onClick={() => setMoreOpen(false)}
                        className="block px-4 py-2 text-sm text-[#1a1a1a]/80 hover:text-[#1a1a1a] hover:bg-[#FFC300]/10 transition-colors"
                      >
                        {link.label}
                      </Link>
                    ))}
                  </div>
                  {/* lg to xl-1: show items 5-7 in More */}
                  <div className="hidden lg:block xl:hidden">
                    {moreItems.map((link) => (
                      <Link
                        key={link.label}
                        to={link.to}
                        onClick={() => setMoreOpen(false)}
                        className="block px-4 py-2 text-sm text-[#1a1a1a]/80 hover:text-[#1a1a1a] hover:bg-[#FFC300]/10 transition-colors"
                      >
                        {link.label}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </nav>
        </div>

        {/* RIGHT: Phone + Request a Quote */}
        <div className="flex flex-nowrap items-center gap-2 md:gap-3 shrink-0">
          <a
            href={PHONE_HREF}
            aria-label={`Call ${PHONE_DISPLAY}`}
            className="inline-flex items-center gap-2 border-2 border-[#1a1a1a] text-[#1a1a1a] px-3 md:px-4 py-2 font-bold text-sm rounded-full hover:bg-[#1a1a1a]/5 transition-colors whitespace-nowrap shrink-0"
          >
            <Phone className="w-4 h-4 shrink-0" />
            <span className="hidden sm:inline">{PHONE_DISPLAY}</span>
          </a>
          <Link
            to="/get-quote"
            className="inline-flex bg-[#1a1a1a] text-[#FFC300] px-4 md:px-5 py-2 font-bold text-sm rounded-full hover:opacity-90 transition-opacity whitespace-nowrap shrink-0"
          >
            Request a Quote
          </Link>
        </div>
      </div>

      {/* Mobile nav drawer */}
      {menuOpen && (
        <nav className="md:hidden border-t border-[#1a1a1a]/10 bg-[#FFC300]">
          <div className="container py-4 flex flex-col gap-3">
            {navLinks.map((link) => (
              <Link
                key={link.label}
                to={link.to}
                onClick={() => setMenuOpen(false)}
                className="text-[#1a1a1a] font-bold text-base py-2 border-b border-[#1a1a1a]/10"
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/get-quote"
              onClick={() => setMenuOpen(false)}
              className="text-[#1a1a1a] font-bold text-base py-2 border-b border-[#1a1a1a]/10"
            >
              Get a Quote
            </Link>
            <a
              href={PHONE_HREF}
              className="flex items-center gap-2 text-[#1a1a1a] font-bold text-base py-2"
            >
              <Phone className="w-5 h-5" />
              {PHONE_DISPLAY}
            </a>
          </div>
        </nav>
      )}
    </header>
  );
};

export default Header;
